import React, { useEffect, useState, useMemo, useContext } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../contexts/AuthContext';
import siteService from '../../services/siteService';
import participantService from '../../services/participantService';
import { ParticipantActiveBadge } from '../common/ParticipantStatusBadge';
import { Building, Globe, User, Mail, UserPlus, Edit2, ChevronLeft, ChevronUp, ChevronDown, Search, Filter, Power, PowerOff, Calendar, Loader2, Users, Bell, Activity, CheckCircle2, AlertTriangle } from 'lucide-react';
import PageHeader from '../Shared/PageHeader';
import EmptyState from '../ui/EmptyState';
import PageLoader from '../ui/PageLoader';
import ContextualNotificationToggle from '../Notifications/ContextualNotificationToggle';
import { toast } from 'react-hot-toast';
import hasUserRole from '../../utils/UserUtil'
import permissionsService from '../../services/permissionsService'
import AddUserModal from '../User/AddUserModal'
import BulkCreateParticipantsModal from '../Participant/BulkCreateParticipantsModal'
import { Tab } from '@headlessui/react';
import {
  PARTICIPANTS_PER_SITE_PAGE,
  paginateSiteParticipants,
} from '../../utils/siteParticipantPagination';

const SITE_TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'notifications', label: 'Notifications', icon: Bell },
];

// FECOA-1609: small badge for a participant's survey-window generation status.
const renderGenerationBadge = (gen) => {
  if (!gen || gen.status === 'complete') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-success-600 dark:text-success-400">
        <CheckCircle2 size={14} /> Ready
      </span>
    );
  }
  if (gen.status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-danger-600 dark:text-danger-400" title={gen.error || 'Generation failed'}>
        <AlertTriangle size={14} /> Failed
      </span>
    );
  }
  // pending / in_progress
  return (
    <span className="inline-flex items-center gap-1 text-xs text-warning-600 dark:text-warning-400">
      <Loader2 size={14} className="animate-spin" /> Building…
    </span>
  );
};

const SiteDetailsPage = () => {
  const { studyID, siteID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useContext(AuthContext);
  const searchParams = new URLSearchParams(location.search);
  const [participants, setParticipants] = useState([]);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'participant_id', direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: []
  });
  const [siteData, setSiteData] = useState({
    site_name: '',
    institution: '',
    country: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: ''
  });
  const [permissions, setPermissions] = useState({
    canChangeSite: false,
    canAddParticipant: false,
    canActivateParticipant: false,
    canDeactivateParticipant: false,
    canAddUser: false,
    canBulkCreateParticipants: false,
  });

  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isBulkCreateOpen, setIsBulkCreateOpen] = useState(false);
  // FECOA-1609: window-generation status keyed by participant UUID.
  const [genStatusMap, setGenStatusMap] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  const getActiveTabFromURL = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      const t = SITE_TABS.findIndex(tab => tab.id === tabParam);
      if (t >= 0) return t;
    }
    if (location.hash) {
      const id = location.hash.substring(1);
      const t = SITE_TABS.findIndex(tab => tab.id === id);
      if (t >= 0) return t;
    }
    return 0;
  };

  useEffect(() => {
    const newTab = getActiveTabFromURL();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.search, location.hash]);

  const updateActiveTab = (tab) => {
    const tabId = SITE_TABS[tab].id;
    if (tabId === 'overview')
      navigate(`/studies/${studyID}/sites/${siteID}`, { replace: true });
    else
      navigate(`/studies/${studyID}/sites/${siteID}#${tabId}`, { replace: true });
    setActiveTab(tab);
  };

  useEffect(() => {
    const fetchSiteDetails = async () => {
      try {
        const data = await siteService.getSiteDetailsForStudy(studyID, siteID);
        setSiteData(data);
        setError('');
      } catch (error) {
        console.error('Error fetching site details:', error);
        setError('An error occurred while fetching the site details.');
      }
    };

    const fetchParticipants = async () => {
      try {
        const data = await participantService.getParticipantsForSite(studyID, siteID);
        console.log('Fetched participants:', data);
        const participantsArray = Array.isArray(data.participants) ? data.participants : [];
        console.log('Participants array:', participantsArray);
        setParticipants(participantsArray);
        setFilteredParticipants(participantsArray);
        setError('');
      } catch (error) {
        console.error('Error fetching participants:', error);
        setError('An error occurred while fetching the participants.');
      } finally {
        setLoading(false);
      }
    };

    if (studyID && siteID) {
      setLoading(true);
      fetchSiteDetails();
      fetchParticipants();
    }
  }, [studyID, siteID]);

  // FECOA-1609: poll window-generation status for this study and keep polling
  // while any participant is still generating; stop once all are terminal.
  useEffect(() => {
    if (!studyID) return;
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const data = await participantService.getGenerationStatus({ studyID });
        if (cancelled) return;
        const map = {};
        (data.participants || []).forEach((p) => { map[p.participant_id] = p; });
        setGenStatusMap(map);
        const stillBuilding = (data.participants || []).some(
          (p) => p.status === 'pending' || p.status === 'in_progress'
        );
        if (stillBuilding) timer = setTimeout(poll, 5000);
      } catch {
        // Non-fatal; the column just won't show a badge this cycle.
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [studyID, participants.length]);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      if (user) {
        try {
          const [hasChangeSite, hasAddParticipant, hasActivateParticipant, hasDeactivateParticipant, hasAddUser, hasBulkCreate] = await Promise.all([
            permissionsService.hasPermission(permissionsService.PERMISSIONS.CHANGE_SITE),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.ADD_PARTICIPANT),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.ACTIVATE_PARTICIPANT),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.DEACTIVATE_PARTICIPANT),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.ADD_USER),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.BULK_CREATE_PARTICIPANTS),
          ]);

          setPermissions({
            canChangeSite: hasChangeSite,
            canAddParticipant: hasAddParticipant,
            canActivateParticipant: hasActivateParticipant,
            canDeactivateParticipant: hasDeactivateParticipant,
            canAddUser: hasAddUser,
            canBulkCreateParticipants: hasBulkCreate,
          });
        } catch (error) {
          console.error('Error checking permissions:', error);
          setPermissions({
            canChangeSite: false,
            canAddParticipant: false,
            canActivateParticipant: false,
            canDeactivateParticipant: false,
            canAddUser: false,
            canBulkCreateParticipants: false,
          });
        }
      }
    };
    
    checkPermissions();
  }, [user]);

  useEffect(() => {
    const filtered = participants.filter(participant => {
      const searchTermLower = searchTerm.toLowerCase();
      const matchesSearch = (
        (participant.participant_id && participant.participant_id.toLowerCase().includes(searchTermLower)) ||
        (participant.participant_number && participant.participant_number.toString().toLowerCase().includes(searchTermLower)) ||
        (participant.is_active !== undefined && participant.is_active.toString().toLowerCase().includes(searchTermLower))
      );
      
      const matchesFilters = 
        (filters.status.length === 0 || 
          (participant.is_active && filters.status.includes('active')) ||
          (!participant.is_active && filters.status.includes('inactive')));

      return matchesSearch && matchesFilters;
    });
    setFilteredParticipants(filtered);
  }, [searchTerm, participants, filters]);

  const extractParticipantNumber = (participantId) => {
    const match = participantId.match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const sortedParticipants = useMemo(() => {
    let sortableParticipants = [...filteredParticipants];
    if (sortConfig.key) {
      sortableParticipants.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'id') {
          aValue = extractParticipantNumber(a.id);
          bValue = extractParticipantNumber(b.id);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableParticipants;
  }, [filteredParticipants, sortConfig]);

  const paginatedParticipants = useMemo(
    () => paginateSiteParticipants(sortedParticipants, currentPage),
    [sortedParticipants, currentPage]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, sortConfig]);

  const handleActivateDeactivate = (participant) => {
    if (participant.is_active) {
      navigate(`/studies/${studyID}/sites/${siteID}/participant/${participant.id}/deactivate`);
    } else {
      navigate(`/studies/${studyID}/sites/${siteID}/participant/${participant.id}/activate`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const toggleFilter = (filterType, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterType]: prevFilters[filterType].includes(value)
        ? prevFilters[filterType].filter(item => item !== value)
        : [...prevFilters[filterType], value]
    }));
  };

  const renderFilterOptions = () => {
    const statusOptions = ['active', 'inactive'];

    return (
      <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-900/40">
        <div>
          <h3 className="mb-2 text-sm font-semibold capitalize text-gray-900 dark:text-gray-100">Status</h3>
          <div className="flex flex-wrap gap-2">
            {statusOptions.map(option => (
              <button
                key={option}
                onClick={() => toggleFilter('status', option)}
                aria-pressed={filters.status.includes(option)}
                className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                  filters.status.includes(option)
                    ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-primary-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-primary-500'
                }`}
              >
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="card w-full max-w-md">
          <EmptyState
            icon={AlertTriangle}
            tone="danger"
            title="Error"
            description={error}
            action={
              <button
                onClick={() => window.location.reload()}
                className="btn btn-primary"
              >
                Retry
              </button>
            }
          />
        </div>
      </div>
    );
  }

  if (!participants?.length) {
    return (
      <div>
        <Link
          to={`/studies/${studyID}#sites`}
          className="back-link"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back to Study</span>
        </Link>
        <div className="card overflow-hidden">
          <div className="card-band">
            <h2 className="card-band-title">Site Details</h2>
          </div>
          <div className="p-6">
            <EmptyState
              icon={Users}
              title="No Participants Found"
              description={permissions.canAddParticipant
                ? 'This site currently has no participants enrolled. Add your first participant to get started with data collection and management.'
                : 'This site currently has no participants enrolled.'}
              action={permissions.canAddParticipant ? (
                <Link
                  to={`/studies/${studyID}/sites/${siteID}/participants/add-participant`}
                  className="btn btn-primary"
                >
                  <UserPlus className="h-4 w-4" />
                  Add First Participant
                </Link>
              ) : null}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        <Link
          to={`/studies/${studyID}#sites`}
          className="back-link"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back to Study</span>
        </Link>
        <PageHeader
          icon={Building}
          title={siteData.site_name || 'Site'}
          subtitle="View site details, participants, and notifications"
        >
          {permissions.canChangeSite && (
            <Link
              to={`/studies/${studyID}/sites/${siteID}/edit`}
              className="btn btn-primary"
            >
              <Edit2 className="h-4 w-4" />
              Edit Site
            </Link>
          )}
          {permissions.canAddUser && (
            <button
              onClick={() => setIsAddUserModalOpen(true)}
              className="btn btn-secondary"
            >
              <UserPlus className="h-4 w-4" />
              Add User
            </button>
          )}
        </PageHeader>
      </motion.div>

      <div className="card overflow-hidden">
        <div className="card-band">
          <h2 className="card-band-title">Site Details</h2>
        </div>
        <div className="p-6">
          <Tab.Group selectedIndex={activeTab} onChange={updateActiveTab}>
            <Tab.List className="tabs-list mb-6">
              {SITE_TABS.map((tab) => (
                <Tab
                  key={tab.id}
                  className={({ selected }) => `tab ${selected ? 'tab-active' : ''}`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              {/* Overview Tab */}
              <Tab.Panel>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="flex items-start gap-3">
                      <Building className="mt-0.5 flex-shrink-0 text-primary-600 dark:text-primary-400" size={18} />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Institution</p>
                        <p className="break-words text-base font-semibold text-gray-900 dark:text-gray-100">{siteData.institution}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Globe className="mt-0.5 flex-shrink-0 text-primary-600 dark:text-primary-400" size={18} />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Country</p>
                        <p className="break-words text-base font-semibold text-gray-900 dark:text-gray-100">{siteData.country}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <User className="mt-0.5 flex-shrink-0 text-primary-600 dark:text-primary-400" size={18} />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Contact</p>
                        <p className="break-words text-base font-semibold text-gray-900 dark:text-gray-100">{`${siteData.contact_first_name} ${siteData.contact_last_name}`}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail className="mt-0.5 flex-shrink-0 text-primary-600 dark:text-primary-400" size={18} />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Contact Email</p>
                        <p className="break-words text-base font-semibold text-gray-900 dark:text-gray-100">{siteData.contact_email}</p>
                      </div>
                    </div>
                  </div>

                  <div id="participants-section" className="mt-8">
                    <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100">Participants</h3>
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="relative w-full sm:w-72">
                        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                        <input
                          type="text"
                          placeholder="Search participants..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="input pl-10"
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setShowFilters(!showFilters)}
                          aria-pressed={showFilters}
                          className={`btn ${showFilters ? 'border border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-400/30 dark:bg-primary-400/10 dark:text-primary-300' : 'btn-secondary'}`}
                        >
                          <Filter size={16} />
                          {showFilters ? 'Hide Filters' : 'Show Filters'}
                        </button>
                        {permissions.canAddParticipant && (
                        <Link
                          to={`/studies/${studyID}/sites/${siteID}/participants/add-participant`}
                          className="btn btn-primary"
                        >
                          <UserPlus size={16} />
                          Add Participant
                        </Link>
                        )}
                        {permissions.canBulkCreateParticipants && (
                        <button
                          onClick={() => setIsBulkCreateOpen(true)}
                          className="btn btn-secondary"
                        >
                          <Users size={16} />
                          Bulk Add
                        </button>
                        )}
                      </div>
                    </div>

                    {showFilters && renderFilterOptions()}

                    <div>
                      {sortedParticipants.length === 0 ? (
                        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                          <div className="card-band">
                            <h2 className="card-band-title">Search Results</h2>
                          </div>
                          <EmptyState
                            icon={Search}
                            title="No Matching Participants"
                            description="No participants match your current search criteria. Try adjusting your filters or search terms."
                          />
                        </div>
                      ) : (
                        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                          <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead>
                              <tr>
                                <th
                                  className="th-cell cursor-pointer"
                                  onClick={() => requestSort('participant_id')}
                                >
                                  <span className="inline-flex items-center gap-1">
                                    Participant ID
                                    {sortConfig.key === 'participant_id' && (
                                      sortConfig.direction === 'ascending' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                    )}
                                  </span>
                                </th>
                                <th className="th-cell">Participant Number</th>
                                <th className="th-cell">Status</th>
                                <th className="th-cell">Setup</th>
                                <th className="th-cell">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-800">
                              {paginatedParticipants.items.map(participant => (
                                <tr key={participant.id} className="tr-interactive">
                                  <td className="td-cell whitespace-nowrap">
                                    <Link
                                      to={`/studies/${studyID}/sites/${siteID}/participant/${participant.id || ''}`}
                                      className="link"
                                    >
                                      {participant.participant_id || 'N/A'}
                                    </Link>
                                  </td>
                                  <td className="td-cell whitespace-nowrap">
                                    <span className="text-gray-700 dark:text-gray-200">
                                      {participant.participant_id ? (participant.participant_id.split('-')[1] || 'N/A') : 'N/A'}
                                    </span>
                                  </td>
                                  <td className="td-cell whitespace-nowrap">
                                    <ParticipantActiveBadge isActive={!!participant.is_active} />
                                  </td>
                                  <td className="td-cell whitespace-nowrap">
                                    {renderGenerationBadge(genStatusMap[participant.id])}
                                  </td>
                                  <td className="td-cell whitespace-nowrap">
                                  {((participant.is_active && permissions.canDeactivateParticipant) || (!participant.is_active && permissions.canActivateParticipant)) && (
                                    <button
                                      onClick={() => handleActivateDeactivate(participant)}
                                      className={`inline-flex rounded-lg p-1.5 transition-colors ${participant.is_active
                                        ? 'text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-500/10'
                                        : 'text-success-600 hover:bg-success-50 dark:text-success-400 dark:hover:bg-success-500/10'
                                      }`}
                                      title={participant.is_active ? 'Deactivate' : 'Activate'}
                                    >
                                      {participant.is_active ? <PowerOff size={16} /> : <Power size={16} />}
                                    </button>
                                  )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>

                          {sortedParticipants.length > PARTICIPANTS_PER_SITE_PAGE && (
                            <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Showing <span className="font-medium text-gray-900 dark:text-gray-100">{paginatedParticipants.startItem}</span> to{' '}
                                <span className="font-medium text-gray-900 dark:text-gray-100">{paginatedParticipants.endItem}</span> of{' '}
                                <span className="font-medium text-gray-900 dark:text-gray-100">{paginatedParticipants.totalItems}</span> participants
                              </p>
                              <nav className="inline-flex items-center gap-1" aria-label="Participant pagination">
                                <button
                                  type="button"
                                  onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                                  disabled={paginatedParticipants.currentPage === 1}
                                  className="btn btn-ghost btn-sm"
                                >
                                  Previous
                                </button>
                                {Array.from({ length: paginatedParticipants.totalPages }).map((_, index) => {
                                  const pageNumber = index + 1;
                                  const isActivePage = paginatedParticipants.currentPage === pageNumber;

                                  return (
                                    <button
                                      key={pageNumber}
                                      type="button"
                                      onClick={() => setCurrentPage(pageNumber)}
                                      className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg px-2 text-sm font-medium transition-colors ${
                                        isActivePage
                                          ? 'bg-primary-600 text-white dark:bg-primary-500'
                                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/[0.06]'
                                      }`}
                                      aria-current={isActivePage ? 'page' : undefined}
                                    >
                                      {pageNumber}
                                    </button>
                                  );
                                })}
                                <button
                                  type="button"
                                  onClick={() => setCurrentPage(page => Math.min(paginatedParticipants.totalPages, page + 1))}
                                  disabled={paginatedParticipants.currentPage === paginatedParticipants.totalPages}
                                  className="btn btn-ghost btn-sm"
                                >
                                  Next
                                </button>
                              </nav>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              </Tab.Panel>

              {/* Notifications Tab */}
              <Tab.Panel>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="space-y-6">
                    <div>
                      <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">Site Notifications</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Configure which notifications you want to receive for events at this site.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <ContextualNotificationToggle
                        eventKey="survey.missed"
                        label="Missed Surveys"
                        description="Get notified when participants miss survey deadlines"
                        studyId={studyID}
                        siteId={siteID}
                      />
                      
                      <ContextualNotificationToggle
                        eventKey="participant.created"
                        label="New Participants"
                        description="Know when new participants are enrolled"
                        studyId={studyID}
                        siteId={siteID}
                      />
                      
                      <ContextualNotificationToggle
                        eventKey="participant.activated"
                        label="Participant Activations"
                        description="Be informed when participants are activated"
                        studyId={studyID}
                        siteId={siteID}
                      />

                      <ContextualNotificationToggle
                        eventKey="participant.deactivated"
                        label="Participant Deactivations"
                        description="Get alerts when participants are deactivated"
                        studyId={studyID}
                        siteId={siteID}
                      />
                    </div>

                    <div className="flex flex-col gap-2 border-t border-gray-200 pt-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Notifications specific to <strong className="font-semibold text-gray-700 dark:text-gray-200">{siteData.site_name}</strong>
                      </p>
                      <Link
                        to="/profile?tab=notifications"
                        className="link text-sm"
                      >
                        Manage all notifications →
                      </Link>
                    </div>
                  </div>
                </motion.div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>

      {/* Add User Modal */}
      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSuccess={() => {
          setIsAddUserModalOpen(false);
        }}
        sponsorId={siteData?.sponsor?.id}
        studyId={studyID}
        siteId={siteData?.id}
      />
      {/* Bulk Create Participants Modal */}
      <BulkCreateParticipantsModal
        isOpen={isBulkCreateOpen}
        onClose={() => setIsBulkCreateOpen(false)}
        onSuccess={() => { setIsBulkCreateOpen(false); window.location.reload(); }}
        studyId={studyID}
        siteId={siteID}
        sites={[siteData]}
        studyName=""
        sponsorCode=""
      />
    </div>
  );
};

export default SiteDetailsPage;